import { useCallback, useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Pencil, Trash2, ShieldCheck, Wrench, User, Download, Upload, KeyRound, Package } from "lucide-react";

const ROLE_META = {
  supervisor: { label: "Supervisor", icon: ShieldCheck, color: "text-orange-700 dark:text-orange-400 bg-orange-500/10 border-orange-500/30" },
  tecnico:    { label: "Técnico",    icon: Wrench,      color: "text-blue-700 dark:text-blue-400 bg-blue-500/10 border-blue-500/30" },
  solicitante:{ label: "Solicitante",icon: User,        color: "text-slate-700 dark:text-slate-400 bg-slate-500/10 border-slate-500/30" },
  almoxarife: { label: "Almoxarife", icon: Package,     color: "text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 border-emerald-500/30" },
};

export default function Users() {
  const { user: currentUser } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ id: "", name: "", email: "", role: "solicitante", password: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => api.get("/users").then((r) => setItems(r.data)).catch(() => {}), []);
  const [resets, setResets] = useState([]);
  const loadResets = useCallback(() => api.get("/admin/password-resets").then((r) => setResets(r.data)).catch(() => {}), []);
  useEffect(() => { load(); loadResets(); }, [load, loadResets]);

  if (currentUser && !["supervisor", "almoxarife"].includes(currentUser.role)) {
    return <Navigate to="/" replace />;
  }

  const openEdit = (u) => {
    setForm({ id: u.id, name: u.name, email: u.email, role: u.role, password: "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Nome obrigatório");
    if (!form.email.trim()) return toast.error("Email obrigatório");
    setSaving(true);
    try {
      const payload = { name: form.name, email: form.email, role: form.role };
      if (form.password) payload.password = form.password;
      await api.patch(`/users/${form.id}`, payload);
      toast.success("Usuário atualizado");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const remove = async (u) => {
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} excluído`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const exportAll = async () => {
    try {
      const { data } = await api.get("/admin/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `pcm-backup-${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      toast.success("Backup exportado. Faça o download e importe no ambiente de produção.");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const importFile = async (file, mode) => {
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      const { data: result } = await api.post("/admin/import", { data, mode });
      const totals = Object.entries(result.stats || {})
        .map(([k, v]) => `${k}: +${v.inserted}/~${v.updated}`)
        .join("  ·  ");
      toast.success(`Importação (${mode}) concluída — ${totals}`);
      load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const groups = [
    { role: "supervisor", label: "Supervisores" },
    { role: "tecnico", label: "Técnicos" },
    { role: "almoxarife", label: "Almoxarifes" },
    { role: "solicitante", label: "Solicitantes" },
  ];

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Administração</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Usuários</h1>
          <p className="text-muted-foreground mt-1">Gerencie contas: edite nome, email, papel, senha ou exclua cadastros incorretos. Apenas supervisores e almoxarifes têm acesso.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={exportAll} variant="outline" className="rounded-sm gap-2" data-testid="export-all-btn">
            <Download className="w-4 h-4" /> Exportar Backup
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline" className="rounded-sm gap-2" data-testid="import-btn">
                <Upload className="w-4 h-4" /> Importar Backup
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Importar Backup JSON</AlertDialogTitle>
                <AlertDialogDescription>
                  <span className="block mb-3">Escolha o modo de importação e depois selecione o arquivo <code className="font-mono-tag text-orange-600">.json</code> exportado do preview:</span>
                  <span className="block mb-2 text-sm"><b>Mesclar (recomendado)</b>: adiciona só o que não existe aqui. Preserva dados já cadastrados na produção.</span>
                  <span className="block text-sm text-red-600"><b>Substituir</b>: apaga tudo (menos você) e restaura do backup. Use com cuidado.</span>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <div className="flex flex-col gap-2 mt-2">
                <label className="rounded-sm border border-input hover:bg-secondary cursor-pointer px-4 py-2 text-sm font-medium text-center">
                  Mesclar dados do arquivo…
                  <input type="file" accept=".json,application/json" hidden onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0], "merge")} data-testid="import-merge-input" />
                </label>
                <label className="rounded-sm border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 cursor-pointer px-4 py-2 text-sm font-medium text-center">
                  Substituir tudo pelo arquivo…
                  <input type="file" accept=".json,application/json" hidden onChange={(e) => e.target.files?.[0] && importFile(e.target.files[0], "replace")} data-testid="import-replace-input" />
                </label>
              </div>
              <AlertDialogFooter>
                <AlertDialogCancel>Fechar</AlertDialogCancel>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {groups.map((g) => {
        const list = items.filter((u) => u.role === g.role);
        const meta = ROLE_META[g.role];
        const Icon = meta.icon;
        return (
          <Card key={g.role} className="rounded-sm shadow-none">
            <div className="p-5 border-b flex items-center gap-3">
              <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center">
                <Icon className="w-4 h-4 text-orange-500" />
              </div>
              <div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Papel</div>
                <h3 className="font-heading text-xl font-bold">{g.label}</h3>
              </div>
              <Badge variant="outline" className="rounded-sm ml-auto">{list.length} usuário{list.length === 1 ? "" : "s"}</Badge>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nome</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead className="w-40 text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {list.length === 0 ? (
                  <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-6">Nenhum usuário</TableCell></TableRow>
                ) : list.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">
                      {u.name}
                      {u.id === currentUser?.id && <span className="ml-2 text-[10px] tracking-widest uppercase text-orange-500 font-semibold">(você)</span>}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">{u.email}</TableCell>
                    <TableCell className="text-right space-x-2">
                      <Button size="sm" variant="outline" onClick={() => openEdit(u)} className="rounded-sm gap-1.5 h-8" data-testid={`edit-user-${u.id}`}>
                        <Pencil className="w-3.5 h-3.5" /> Editar
                      </Button>
                      {u.id !== currentUser?.id && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="sm" variant="outline" className="rounded-sm h-8 gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10" data-testid={`delete-user-${u.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Excluir {u.name}?</AlertDialogTitle>
                              <AlertDialogDescription>
                                <span className="block mb-2">Isso apagará o cadastro e todas as notificações desse usuário.</span>
                                <span className="block text-xs">OSs históricas em que ele apareceu como técnico ou solicitante permanecem com o nome atual. Ação não pode ser desfeita.</span>
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancelar</AlertDialogCancel>
                              <AlertDialogAction onClick={() => remove(u)} className="bg-red-600 hover:bg-red-700" data-testid={`confirm-delete-user-${u.id}`}>
                                Excluir Usuário
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        );
      })}

      <Card className="rounded-sm shadow-none" data-testid="resets-card">
        <div className="p-5 border-b flex items-center gap-3">
          <div className="w-8 h-8 rounded-sm bg-secondary flex items-center justify-center">
            <KeyRound className="w-4 h-4 text-orange-500" />
          </div>
          <div>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-muted-foreground">Segurança</div>
            <h3 className="font-heading text-xl font-bold">Pedidos de Reset de Senha</h3>
          </div>
          <Badge variant="outline" className="rounded-sm ml-auto">{resets.length} pedido{resets.length === 1 ? "" : "s"}</Badge>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Usuário</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Solicitado em</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {resets.length === 0 ? (
              <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-6">Nenhum pedido recente</TableCell></TableRow>
            ) : resets.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{r.email}</TableCell>
                <TableCell className="text-sm">{new Date(r.created_at).toLocaleString("pt-BR")}</TableCell>
                <TableCell>
                  {r.used ? <Badge className="rounded-sm bg-green-500/10 text-green-700 border-green-500/30 border" variant="outline">Concluído</Badge>
                   : r.expired ? <Badge className="rounded-sm bg-slate-500/10 text-slate-700 border-slate-500/30 border" variant="outline">Expirado</Badge>
                   : <Badge className="rounded-sm bg-yellow-500/10 text-yellow-700 border-yellow-500/30 border" variant="outline">Pendente</Badge>}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle>Editar Usuário</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm mt-1.5" data-testid="user-name-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="rounded-sm mt-1.5" data-testid="user-email-input" />
            </div>
            <div>
              <Label>Papel</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="rounded-sm mt-1.5" data-testid="user-role-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="solicitante">Solicitante</SelectItem>
                  <SelectItem value="almoxarife">Almoxarife</SelectItem>
                  <SelectItem value="tecnico">Técnico</SelectItem>
                  <SelectItem value="supervisor">Supervisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nova senha (opcional)</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Deixe em branco para manter" minLength={6} className="rounded-sm mt-1.5" data-testid="user-password-input" />
              <div className="text-[10px] text-muted-foreground mt-1">Mínimo 6 caracteres. Só preencha se quiser trocar.</div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={save} disabled={saving} className="rounded-sm bg-orange-500 hover:bg-orange-600 text-white" data-testid="user-save-btn">
              {saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
